import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo, useRef, useState } from "react";

import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import {
  AI_INSIGHTS_ENTITLEMENT_KEY,
  buildAiInsightsToggleSuccessMessage,
  formatAiInsightsResolvedFromLabel,
  resolveAiInsightsEntitlementState,
} from "@/pages/AdminV2/Settings/aiInsightsAccessPageHelpers";
import {
  useGetAdminV2EntitlementsQuery,
  useGetAdminV2MeQuery,
  useUpsertAdminV2EntitlementMutation,
} from "@/store/api/baseApi";
import {
  VNextButton,
  VNextCard,
  VNextDialog,
  VNextInput,
  VNextKeyValueList,
  VNextNotice,
  VNextStat,
} from "@/vnext/primitives";
import { ProductWorkspaceChrome } from "@/vnext/routes/product/ProductWorkspaceChrome";

const UUID_LIKE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface PendingAiInsightsAccessChange {
  currentStateLabel: "Enabled" | "Disabled";
  effectCopy: string;
  nextIsEnabled: boolean;
  organisationId: string;
  resolvedFromLabel: string;
  targetStateLabel: "Enabled" | "Disabled";
}

export default function AiInsightsRoute() {
  const { data: meData } = useGetAdminV2MeQuery();
  const { hasPermission } = useAdminPermissions(meData?.permissionKeys);
  const canManageEntitlements = hasPermission("entitlements.manage");
  const [organisationIdInput, setOrganisationIdInput] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const accessChangeInFlightRef = useRef(false);
  const [pendingAccessChange, setPendingAccessChange] =
    useState<PendingAiInsightsAccessChange | null>(null);
  const [isConfirmingAccessChange, setIsConfirmingAccessChange] =
    useState(false);
  const scopedOrganisationId = organisationIdInput.trim();
  const hasOrganisationId = scopedOrganisationId.length > 0;
  const hasValidOrganisationId =
    hasOrganisationId && UUID_LIKE_PATTERN.test(scopedOrganisationId);
  const entitlementsQuery = useGetAdminV2EntitlementsQuery(
    hasValidOrganisationId
      ? {
          includeGlobal: true,
          limit: 100,
          organisationId: scopedOrganisationId,
        }
      : skipToken,
  );
  const [upsertEntitlement, upsertState] =
    useUpsertAdminV2EntitlementMutation();
  const entitlementState = useMemo(
    () =>
      resolveAiInsightsEntitlementState({
        entitlementKey: AI_INSIGHTS_ENTITLEMENT_KEY,
        entitlements: entitlementsQuery.data?.entitlements,
        scopedOrganisationId,
      }),
    [entitlementsQuery.data?.entitlements, scopedOrganisationId],
  );
  const entitlementStateUnavailable =
    hasValidOrganisationId &&
    (entitlementsQuery.isLoading ||
      entitlementsQuery.isFetching ||
      entitlementsQuery.isError);

  const openAccessChangeConfirmation = () => {
    if (
      !canManageEntitlements ||
      !hasValidOrganisationId ||
      entitlementStateUnavailable ||
      upsertState.isLoading ||
      isConfirmingAccessChange
    ) {
      return;
    }

    const nextIsEnabled = !entitlementState.effectiveAiInsightsEnabled;
    setPendingAccessChange({
      currentStateLabel: entitlementState.effectiveAiInsightsEnabled
        ? "Enabled"
        : "Disabled",
      effectCopy: nextIsEnabled
        ? "This creates an organisation override that turns AI insights on for this organisation."
        : "This creates an organisation override that turns AI insights off for this organisation.",
      nextIsEnabled,
      organisationId: scopedOrganisationId,
      resolvedFromLabel: formatAiInsightsResolvedFromLabel(
        entitlementState.resolvedFrom,
      ),
      targetStateLabel: nextIsEnabled ? "Enabled" : "Disabled",
    });
    setFeedback(null);
  };

  const closeAccessChangeConfirmation = () => {
    if (accessChangeInFlightRef.current || !pendingAccessChange) {
      return;
    }

    setFeedback("AI insights access change cancelled.");
    setPendingAccessChange(null);
  };

  const confirmAccessChange = async () => {
    if (!pendingAccessChange || accessChangeInFlightRef.current) {
      return;
    }

    accessChangeInFlightRef.current = true;
    setIsConfirmingAccessChange(true);
    setFeedback(null);
    try {
      await upsertEntitlement({
        entitlementKey: AI_INSIGHTS_ENTITLEMENT_KEY,
        isEnabled: pendingAccessChange.nextIsEnabled,
        organisationId: pendingAccessChange.organisationId,
        source: "admin",
        value: {},
      }).unwrap();
      setFeedback(
        buildAiInsightsToggleSuccessMessage(pendingAccessChange.nextIsEnabled),
      );
      setPendingAccessChange(null);
      await entitlementsQuery.refetch();
    } catch {
      setFeedback("AI insights access could not be updated.");
      setPendingAccessChange(null);
    } finally {
      accessChangeInFlightRef.current = false;
      setIsConfirmingAccessChange(false);
    }
  };

  return (
    <ProductWorkspaceChrome
      subtitle="Manage AI insights access for one organisation at a time."
      title="AI insights access"
    >
      <div className="vnext-stack">
        {pendingAccessChange ? (
          <VNextDialog
            description="Review before changing AI insights access."
            footer={
              <>
                <VNextButton
                  disabled={isConfirmingAccessChange}
                  onClick={closeAccessChangeConfirmation}
                  tone="secondary"
                >
                  Cancel
                </VNextButton>
                <VNextButton
                  disabled={isConfirmingAccessChange || upsertState.isLoading}
                  onClick={() => void confirmAccessChange()}
                  tone="primary"
                >
                  {isConfirmingAccessChange
                    ? pendingAccessChange.nextIsEnabled
                      ? "Enabling..."
                      : "Disabling..."
                    : pendingAccessChange.nextIsEnabled
                      ? "Confirm enable"
                      : "Confirm disable"}
                </VNextButton>
              </>
            }
            onClose={closeAccessChangeConfirmation}
            open
            title={
              pendingAccessChange.nextIsEnabled
                ? "Enable AI insights access"
                : "Disable AI insights access"
            }
          >
            <div className="vnext-stack">
              <p>{pendingAccessChange.effectCopy}</p>
              <VNextKeyValueList
                items={[
                  {
                    label: "Organisation ID",
                    value: pendingAccessChange.organisationId,
                  },
                  {
                    label: "Current effective access",
                    value: pendingAccessChange.currentStateLabel,
                  },
                  {
                    label: "Resolved from",
                    value: pendingAccessChange.resolvedFromLabel,
                  },
                  {
                    label: "Target access",
                    value: pendingAccessChange.targetStateLabel,
                  },
                ]}
              />
            </div>
          </VNextDialog>
        ) : null}
        {feedback ? <VNextNotice>{feedback}</VNextNotice> : null}
        <VNextCard
          subtitle="Use one organisation UUID at a time. Refresh after a change to confirm the effective state."
          title="Organisation access"
        >
          <div className="vnext-grid vnext-grid--two">
            <label className="vnext-field">
              <span className="vnext-field__label">Organisation ID</span>
              <VNextInput
                onChange={(event) => setOrganisationIdInput(event.target.value)}
                placeholder="e.g. 123e4567-e89b-12d3-a456-426614174000"
                value={organisationIdInput}
              />
            </label>
            <div className="vnext-inline-actions">
              <VNextButton
                disabled={!hasValidOrganisationId}
                onClick={() => void entitlementsQuery.refetch()}
                tone="secondary"
              >
                Refresh access
              </VNextButton>
              <VNextButton
                disabled={
                  !canManageEntitlements ||
                  !hasValidOrganisationId ||
                  entitlementStateUnavailable ||
                  upsertState.isLoading ||
                  isConfirmingAccessChange
                }
                onClick={openAccessChangeConfirmation}
                tone="primary"
              >
                {entitlementState.effectiveAiInsightsEnabled
                  ? "Disable"
                  : "Enable"}
              </VNextButton>
            </div>
          </div>
          {hasOrganisationId && !hasValidOrganisationId ? (
            <div className="vnext-inline-warning">
              Enter a valid organisation UUID before loading AI insights access.
            </div>
          ) : null}
        </VNextCard>

        <VNextCard title="Current state">
          {!hasOrganisationId ? (
            <div className="vnext-empty">
              Enter an organisation UUID to resolve AI insights access for a
              specific organisation.
            </div>
          ) : !hasValidOrganisationId ? (
            <div className="vnext-empty">
              Enter a valid organisation UUID. Example:
              123e4567-e89b-12d3-a456-426614174000.
            </div>
          ) : entitlementsQuery.isLoading ? (
            <div className="vnext-empty">Loading entitlement state…</div>
          ) : entitlementsQuery.isError ? (
            <VNextNotice tone="danger">
              AI insights access could not be loaded for this organisation.
            </VNextNotice>
          ) : (
            <div className="vnext-stats-grid">
              <VNextStat
                label="Effective access"
                value={
                  entitlementState.effectiveAiInsightsEnabled
                    ? "Enabled"
                    : "Disabled"
                }
              />
              <VNextStat
                label="Resolved from"
                value={formatAiInsightsResolvedFromLabel(
                  entitlementState.resolvedFrom,
                )}
              />
            </div>
          )}
        </VNextCard>
      </div>
    </ProductWorkspaceChrome>
  );
}
